/**
 * El idioma de negocio del Back Office: es / en / pt.
 *
 * Es UNO solo para toda la plataforma. Antes de esto había ocho definiciones
 * sueltas del mismo enum (ArcadeLang, LandingLang, TalentMapGreetingLang,
 * DTalkIdioma, DTalkIdiomaOpt, PeopleMailLang, landingLangSchema…) y tres
 * normalizadores distintos (`.slice(0, 2)`, `.find(l => x.startsWith(l))`,
 * un Record casteado a mano). Lo nuevo usa esto; lo viejo se migra cuando se toca.
 *
 * NO es lo mismo que:
 *  · `frontend/src/lib/locale.ts` — BCP-47 para formatear fechas y números.
 *  · `STT_LANG` de `frontend/src/lib/speech-utils.ts` — el acento del dictado.
 */
import { z } from 'zod';

export const APP_LANGS = ['es', 'en', 'pt'] as const;
export type AppLang = (typeof APP_LANGS)[number];

export const appLangSchema = z.enum(APP_LANGS);

/**
 * Cualquier cosa → un idioma soportado. Sirve para `i18n.language` del front
 * ('es-AR' → 'es'), para lo que devuelve un modelo ('PT' → 'pt') y para un
 * query param. Lo que no se reconoce cae a español.
 */
export function normalizeLang(raw: string | null | undefined): AppLang {
  const l = (raw ?? '').slice(0, 2).toLowerCase();
  return (APP_LANGS as readonly string[]).includes(l) ? (l as AppLang) : 'es';
}

/** El texto del idioma pedido, con fallback a español. Para textos que escribe la plataforma. */
export function pickByLang<T>(dict: Record<AppLang, T>, lang: AppLang | null | undefined): T {
  return dict[normalizeLang(lang)] ?? dict.es;
}

/** Cómo se nombra el idioma adentro de un prompt (los modelos responden mejor al nombre propio). */
export const LANG_LABEL: Record<AppLang, string> = {
  es: 'español',
  en: 'English',
  pt: 'português',
};
